"use client";

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

/**
 * The Inaivia cross-app "waffle" switcher (same look and behaviour as the one in
 * @nextgendev319/auth-kit, without the Next.js peer dependency so it drops into any
 * React app).
 *
 * Source of the tiles, in order:
 *   1. `${issuer}/account/my-apps.json` with credentials — the issuer filters to the
 *      apps this user may launch, from its own session cookie (same-site on
 *      *.inaivia.com, so the cookie travels). Works even when this app only holds its
 *      own tokens.
 *   2. Otherwise the public `${issuer}/account/apps.json`, filtered by the `products`
 *      claim of `accessToken` when one is given, else shown in full.
 * Newly registered products appear with no redeploy.
 */

export interface PlatformApp {
  code: string;
  name: string;
  launch_url: string;
  color?: string | null;
}

export interface PlatformAppSwitcherProps {
  /** Code of the app this switcher is embedded in (dimmed, not a link). */
  current: string;
  /** The central issuer, e.g. https://inaiauth.inaivia.com. Nothing renders without it. */
  issuer?: string | null;
  /** The issuer's access token, if this app holds it (fallback filtering only). */
  accessToken?: string | null;
  /** Accessible label / tooltip for the trigger. */
  label?: string;
}

const FALLBACK_COLOR = "#2b7af2";

function entitledCodesFromToken(accessToken?: string | null): string[] {
  if (!accessToken) return [];
  const part = accessToken.split(".")[1];
  if (!part) return [];
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(b64)) as { products?: unknown };
    return Array.isArray(json.products) ? json.products.filter((p) => typeof p === "string") : [];
  } catch {
    return [];
  }
}

function appInitials(name: string): string {
  const [first = "?", second] = name.replace(/^Inaivia\s+/i, "").trim().split(/\s+/).filter(Boolean);
  if (second) return (first.charAt(0) + second.charAt(0)).toUpperCase();
  return first.slice(0, 2).replace(/^./, (c) => c.toUpperCase());
}

async function loadApps(issuer: string): Promise<{ apps: PlatformApp[]; filtered: boolean }> {
  const base = issuer.replace(/\/$/, "");
  try {
    const mine = await fetch(`${base}/account/my-apps.json`, { credentials: "include" });
    if (mine.ok) return { apps: (await mine.json()) as PlatformApp[], filtered: true };
  } catch {
    /* no issuer session or CORS refused — fall through to the public registry */
  }
  const all = await fetch(`${base}/account/apps.json`, { credentials: "omit" });
  if (!all.ok) throw new Error(`app registry ${all.status}`);
  return { apps: (await all.json()) as PlatformApp[], filtered: false };
}

export function PlatformAppSwitcher({
  current,
  issuer,
  accessToken,
  label = "Inaivia apps",
}: PlatformAppSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [registry, setRegistry] = useState<{ apps: PlatformApp[]; filtered: boolean }>({
    apps: [],
    filtered: false,
  });
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!issuer) return;
    let cancelled = false;
    void loadApps(issuer)
      .then((r) => {
        if (!cancelled) setRegistry(r);
      })
      .catch(() => {
        /* registry unavailable — the switcher stays hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [issuer]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const tiles = useMemo(() => {
    if (registry.filtered) return registry.apps;
    const codes = entitledCodesFromToken(accessToken);
    return codes.length ? registry.apps.filter((a) => codes.includes(a.code)) : registry.apps;
  }, [registry, accessToken]);

  if (!tiles.length) return null;

  const trigger: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    border: "none",
    borderRadius: 8,
    background: "transparent",
    color: "currentColor",
    cursor: "pointer",
  };
  const panel: CSSProperties = {
    position: "absolute",
    right: 0,
    top: "calc(100% + 8px)",
    zIndex: 1300,
    background: "#fff",
    color: "#0f172a",
    borderRadius: 14,
    boxShadow: "0 12px 32px rgba(15,23,42,.18)",
    padding: 10,
    width: 288,
  };

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
        style={trigger}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          {[2, 8, 14].flatMap((y) =>
            [2, 8, 14].map((x) => <rect key={`${x}-${y}`} x={x} y={y} width="4" height="4" rx="1" />),
          )}
        </svg>
      </button>
      {open ? (
        <div role="menu" style={panel}>
          <div style={{ padding: "4px 8px", fontSize: 12, fontWeight: 700, color: "#64748b" }}>
            Switch app
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
            {tiles.map((a) => {
              const isCurrent = a.code === current;
              const box: CSSProperties = {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
                padding: "12px 6px",
                borderRadius: 12,
                textDecoration: "none",
                color: "inherit",
                cursor: isCurrent ? "default" : "pointer",
                opacity: isCurrent ? 0.45 : 1,
              };
              const tile = (
                <>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: a.color ?? FALLBACK_COLOR,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: 15,
                    }}
                  >
                    {appInitials(a.name)}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, textAlign: "center", lineHeight: 1.2 }}>
                    {a.name.replace(/^Inaivia\s+/i, "")}
                  </span>
                </>
              );
              return isCurrent ? (
                <div key={a.code} style={box} aria-current="page">
                  {tile}
                </div>
              ) : (
                <a key={a.code} href={a.launch_url} style={box} role="menuitem">
                  {tile}
                </a>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PlatformAppSwitcher;
